import React from "react";
import Box from "@mui/joy/Box";
import Tabs from "@mui/joy/Tabs";
import TabList from "@mui/joy/TabList";
import Tab, { tabClasses } from "@mui/joy/Tab";
import TabPanel from "@mui/joy/TabPanel";
import { Game } from "../../../models/games";
import { gamesListApi } from "../../games/gamesApi";
import { NotificationService } from "../../../common/notification";
import { AnalyticsFiltersState } from "../../bids/analytics/components/AnalyticsFilters";
import { OrdersCharts } from "../OrdersCharts";
import { SummaryTab } from "./tabs/SummaryTab";
import { FailReasonsTab } from "./tabs/FailReasonsTab";
import { TimingTab } from "./tabs/TimingTab";

/** Подписи в порядке ORDERS_ANALYTICS_TAB_SLUGS */
const TAB_LABELS = ["Сводка", "Неуспешные", "Скорость", "Графики"] as const;

const DEFAULT_PERIOD_DAYS = 30;

function initialFilters(): AnalyticsFiltersState {
    const to = new Date();
    const from = new Date();
    from.setDate(from.getDate() - DEFAULT_PERIOD_DAYS);
    from.setHours(0, 0, 0, 0);
    return { from, to, gameId: undefined, take: 20, stuckHours: 24, granularity: "day" };
}

type Props = {
    isActive: boolean;
    currentTabIndex?: number;
    onTabChange?: (index: number) => void;
};

/** Подвкладки «Аналитики» страницы «Ордера»: общий фильтр, данные грузятся только на открытой подвкладке */
export function OrdersAnalytics({ isActive, currentTabIndex = 0, onTabChange }: Props) {
    const [tab, setTab] = React.useState(currentTabIndex);
    const [filters, setFilters] = React.useState<AnalyticsFiltersState>(initialFilters);
    const [games, setGames] = React.useState<Game[]>([]);
    const [gamesLoading, setGamesLoading] = React.useState(true);

    React.useEffect(() => {
        setTab(currentTabIndex);
    }, [currentTabIndex]);

    React.useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const result = await gamesListApi();
                if (cancelled) {
                    return;
                }
                if (result.result) {
                    setGames(result.value ?? []);
                } else {
                    NotificationService.alertError("Не удалось загрузить список игр. Попробуйте позже.");
                }
            } finally {
                if (!cancelled) {
                    setGamesLoading(false);
                }
            }
        })();
        return () => {
            cancelled = true;
        };
    }, []);

    const handleTabChange = (_: React.SyntheticEvent | null, value: number | string | null) => {
        const idx = typeof value === "number" ? value : 0;
        setTab(idx);
        onTabChange?.(idx);
    };

    return (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1, minHeight: 0, flex: 1 }}>
            <Tabs
                value={tab}
                onChange={handleTabChange}
                sx={{ bgcolor: "transparent", flex: 1, display: "flex", flexDirection: "column", minHeight: 0 }}
            >
                <TabList
                    tabFlex={1}
                    size="sm"
                    sx={{
                        justifyContent: "left",
                        overflowX: "auto",
                        flexWrap: "nowrap",
                        [`&& .${tabClasses.root}`]: {
                            fontWeight: "600",
                            flex: "initial",
                            color: "text.tertiary",
                            whiteSpace: "nowrap",
                            [`&.${tabClasses.selected}`]: {
                                bgcolor: "transparent",
                                color: "text.primary",
                                "&::after": {
                                    height: "2px",
                                    bgcolor: "primary.500",
                                },
                            },
                        },
                    }}
                >
                    {TAB_LABELS.map((label, index) => (
                        <Tab key={label} sx={{ borderRadius: "6px 6px 0 0" }} indicatorInset value={index}>
                            {label}
                        </Tab>
                    ))}
                </TabList>

                <TabPanel value={0} sx={{ px: 0, pt: 1.5, pb: 0 }}>
                    <SummaryTab
                        isActive={isActive && tab === 0}
                        filters={filters}
                        onFiltersChange={setFilters}
                        games={games}
                        gamesLoading={gamesLoading}
                    />
                </TabPanel>
                <TabPanel value={1} sx={{ px: 0, pt: 1.5, pb: 0 }}>
                    <FailReasonsTab
                        isActive={isActive && tab === 1}
                        filters={filters}
                        onFiltersChange={setFilters}
                        games={games}
                        gamesLoading={gamesLoading}
                    />
                </TabPanel>
                <TabPanel value={2} sx={{ px: 0, pt: 1.5, pb: 0 }}>
                    <TimingTab
                        isActive={isActive && tab === 2}
                        filters={filters}
                        onFiltersChange={setFilters}
                        games={games}
                        gamesLoading={gamesLoading}
                    />
                </TabPanel>
                <TabPanel value={3} sx={{ px: 0, pt: 1.5, pb: 0 }}>
                    <OrdersCharts />
                </TabPanel>
            </Tabs>
        </Box>
    );
}
